import {Injectable, Injector} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Observable} from 'rxjs/Observable';
import {catchError, filter, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {BpmnEngineService} from '../../services/bpmn-engine.service';
import {Action, select, Store} from '@ngrx/store';
import {
  ProcessUserSelectedPathAction,
  StartExecutionAction,
  UpdateExecutionHistoryParsingStatusAction,
  UpdateProcessExecutionStatus,
  UpdateUserSelectionStatusAction, UserChangedPathAction,
  UserClickedExecutedNodeAction
} from '../actions';
import {
  ProcessUserSelectedElementAction,
  ResumeExecution,
  UserClicksElementsActionTypes
} from '../actions/user-clicks-elements.actions';
import {
  selectBpmnProcessId,
  selectExecutionPath,
  selectProcessInstanceId,
  selectPatientData
} from '../selectors';

import {of} from 'rxjs/observable/of';
import {ProcessInputDataUtils} from '@ispm-client/modules/process-executor/services';
import {SetErrorAction} from '@ispm/shared/store/actions/error.actions';
import {failedToResumeBpmnExecution} from '@ispm-client/modules/process-executor/models/constants';
import {BP_Status, USER_SELECTION_STATUS} from '../state';
import {Node} from '../../models';
import {selectKnowledgeBase} from "@ispm-client/modules/pathways/store/selectors/knowledge-base.selector";

@Injectable()
export class UserClicksElementsEffects {

  constructor(private actions$: Actions,
              private injector: Injector,
              private store$: Store<any>) {
  }


  private getBpmnEngineService(): BpmnEngineService {
    return this.injector.get(BpmnEngineService);
  }

  @Effect()
  userSelectsElementsEffect$: Observable<Action> = this.actions$.pipe(
    ofType<ProcessUserSelectedElementAction>(
      UserClicksElementsActionTypes.PROCESS_USER_SELECTED_ELEMENT
    ),
    withLatestFrom(this.store$.pipe(select(selectExecutionPath))),
    switchMap(([action, executionPath]) =>
      [
        new ResumeExecution({
          id: executionPath.currentUserTaskHolder.userInputTaskId,
          questionsAndAnswers: executionPath.nodesForSelection.find(node => node.nodeId === action.payload.id).questionsAndAnswers,
          userSelectedElement: action.payload.id
        })
      ]
    )
  );

  @Effect()
  userSelectedPathEffect$: Observable<Action> = this.actions$.pipe(
    ofType<ProcessUserSelectedPathAction>(
      UserClicksElementsActionTypes.PROCESS_USER_SELECTED_PATH
    ),
    withLatestFrom(this.store$.pipe(select(selectExecutionPath))),
    switchMap(([action, executionPath]) =>
      [
        new ResumeExecution({
          id: executionPath.currentUserTaskHolder.userInputTaskId,
          questionsAndAnswers: ProcessInputDataUtils.collectQuestionsAndAnswersFromNodes(action.payload.pathFromCommonAncestorToSelected.slice(1))
        })
      ]
    )
  );

  @Effect()
  resumeExecutionEffect$: Observable<Action> = this.actions$.pipe(
    ofType<ResumeExecution>(
      UserClicksElementsActionTypes.RESUME_EXECUTION
    ),
    withLatestFrom(this.store$.pipe(select(selectProcessInstanceId)),
      this.store$.pipe(select(selectKnowledgeBase)).pipe(filter(kb => !!kb),take(1))
    ),
    switchMap(([action, processInstanceId, knowledgeBase]) =>
      this.getBpmnEngineService().updateTaskData(action.payload.id, processInstanceId, action.payload.questionsAndAnswers, knowledgeBase)
        .pipe(
          map((executionStatus) => Object.assign({},executionStatus,{userSelectedElement : action.payload.userSelectedElement})),
          switchMap((executionStatus) =>
            [
              new UpdateUserSelectionStatusAction({status: USER_SELECTION_STATUS.NotInProgress}),
              new UpdateExecutionHistoryParsingStatusAction({status: BP_Status.NotStarted}),
              new UpdateProcessExecutionStatus({processExecutionStatus: executionStatus})
            ]),
          catchError(error => {
            this.store$.dispatch(new UpdateUserSelectionStatusAction({status: USER_SELECTION_STATUS.NotInProgress}));
            return of(new SetErrorAction({
              error,
              devMessage: 'resumeExecutionEffect$: Failed to resume BPMN execution',
              userMessage: failedToResumeBpmnExecution
            }));
          })
        ))
  );

  @Effect()
  userClickedExecutedElementEffect$: Observable<Action> = this.actions$.pipe(
    ofType<UserClickedExecutedNodeAction>(
      UserClicksElementsActionTypes.USER_CLICKED_EXECUTED_NODE
    ),
    withLatestFrom(this.store$.pipe(select(selectBpmnProcessId))),
    withLatestFrom(this.store$.pipe(select(selectPatientData))),
    withLatestFrom(this.store$.pipe(select(selectExecutionPath))),
    switchMap(([[actionAndProcessId, patientData], executedPath]) =>
      [
        new StartExecutionAction({
          bpmnProcessId: actionAndProcessId[1],
          patientData: ProcessInputDataUtils.createProcessInputData(actionAndProcessId[0].payload.id, patientData, executedPath.executedNodes)
        })
      ]
    ));

  @Effect()
  userChangedPathEffect$: Observable<Action> = this.actions$.pipe(
    ofType<UserChangedPathAction>(
      UserClicksElementsActionTypes.USER_CHANGED_PATH
    ),
    withLatestFrom(this.store$.pipe(select(selectBpmnProcessId))),
    withLatestFrom(this.store$.pipe(select(selectPatientData))),
    withLatestFrom(this.store$.pipe(select(selectExecutionPath))),
    switchMap(([[actionAndProcessId, patientData], executedPath]) =>
      [
        new StartExecutionAction({
          bpmnProcessId: actionAndProcessId[1],
          patientData: ProcessInputDataUtils.createProcessInputDataForPath(
            patientData,
            this.combinePaths(executedPath.executedNodes, actionAndProcessId[0].payload.pathFromCommonAncestorToSelected))
        })
      ]
    ));

  private combinePaths(executedNodes: Node[], pathFromCommonAncestorToSelected: Node[]): Node[] {
    const commonAncestor: Node = pathFromCommonAncestorToSelected[0];
    const commonAncestorIndex = executedNodes.findIndex(node => node.nodeId === commonAncestor.nodeId);
    const path = executedNodes.slice(0, commonAncestorIndex + 1);
    path.push(...pathFromCommonAncestorToSelected.slice(1));
    return path;
  }
}
